import React, { useState } from 'react';
import Select from '../Select/Select';
import FilterCard from '../FilterCard/FilterCard';
import { BsBarChartFill } from 'react-icons/bs';
import './FacultyTable.css';

const FacultyTable = ({ data, onShowProjectsChart, onShowBudgetChart, projectButtonContent, budgetButtonContent }) => {
  const [filters, setFilters] = useState({
    yil: [],
    durum: [],
    searchTerm: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  const yearOptions = [
    { value: '2024', label: '2024' },
    { value: '2025', label: '2025' }
  ];

  const statusOptions = [
    { value: 'baslayan', label: 'Başlayan' },
    { value: 'devam', label: 'Devam Eden' },
    { value: 'bitecek', label: 'Bitecek' }
  ];

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'projeSayisi') {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      }
      else if (sortConfig.key === 'butce') {
        aValue = parseFloat(aValue?.replace(/[^0-9.-]+/g,"")) || 0;
        bValue = parseFloat(bValue?.replace(/[^0-9.-]+/g,"")) || 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const filteredData = data.filter(item => {
    const searchMatch = !filters.searchTerm || 
      item.fakulte?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      item.bolum?.toLowerCase().includes(filters.searchTerm.toLowerCase());
    
    const yearMatch = !filters.yil.length || filters.yil.some(y => item.yil === y.value);
    const statusMatch = !filters.durum.length || filters.durum.some(s => item.durum === s.value);

    return searchMatch && yearMatch && statusMatch;
  });

  const sortedData = getSortedData(filteredData);
  const totalProjects = sortedData.reduce((sum, item) => sum + (item.projeSayisi || 0), 0);
  const totalBudget = sortedData.reduce((sum, item) => sum + (parseFloat(item.butce?.replace(/[^0-9.-]+/g,"")) || 0), 0);

  return (
    <div className="faculty-table-wrapper">
      <div className="card-header">
        <h3>Proje Sayı-Bütçe</h3>
      </div>
      <div className="faculty-table-header">
        <FilterCard>
          <FilterCard.Row>
            <FilterCard.Col>
              <div className="visualization-buttons">
                <button 
                  type="button"
                  onClick={() => onShowProjectsChart(sortedData)}
                >
                  <span className="button-content">
                    {projectButtonContent}
                  </span>
                </button>
                <button 
                  type="button"
                  onClick={() => onShowBudgetChart(sortedData)}
                >
                  <span className="button-content">
                    {budgetButtonContent}
                  </span>
                </button>
              </div>
            </FilterCard.Col>
          </FilterCard.Row>
          <FilterCard.Row>
            <FilterCard.Col>
              <label className="select-label">Yıl</label>
              <Select
                value={filters.yil}
                onChange={(value) => handleFilterChange('yil', value)}
                options={yearOptions}
                placeholder="Yıl seçiniz"
              />
            </FilterCard.Col>
            <FilterCard.Col>
              <label className="select-label">Durum</label>
              <Select
                value={filters.durum}
                onChange={(value) => handleFilterChange('durum', value)}
                options={statusOptions}
                placeholder="Durum seçiniz"
              />
            </FilterCard.Col>
            <FilterCard.Col>
              <FilterCard.Input
                label="Birim Ara"
                type="text"
                placeholder="Fakülte veya bölüm adı ile ara..."
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              />
            </FilterCard.Col>
          </FilterCard.Row>
        </FilterCard>
      </div>

      <div className="faculty-table-container">
        <table>
          <thead>
            <tr>
              <th 
                onClick={() => handleSort('fakulte')} 
                className={`sortable ${sortConfig.key === 'fakulte' ? `sort-${sortConfig.direction}` : ''}`}
              >
                Fakülte/Enstitü
                <span className="sort-icon" />
              </th>
              <th 
                onClick={() => handleSort('bolum')} 
                className={`sortable ${sortConfig.key === 'bolum' ? `sort-${sortConfig.direction}` : ''}`}
              >
                Bölüm
                <span className="sort-icon" />
              </th>
              <th 
                onClick={() => handleSort('projeSayisi')} 
                className={`text-center sortable ${sortConfig.key === 'projeSayisi' ? `sort-${sortConfig.direction}` : ''}`}
              >
                Proje Sayısı
                <span className="sort-icon" />
              </th>
              <th 
                onClick={() => handleSort('butce')} 
                className={`text-right sortable ${sortConfig.key === 'butce' ? `sort-${sortConfig.direction}` : ''}`}
              >
                Proje Bütçesi
                <span className="sort-icon" />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, index) => (
              <tr key={index}>
                <td>{item.fakulte}</td>
                <td>{item.bolum}</td>
                <td className="text-center">
                  <span className="project-count">{item.projeSayisi}</span>
                </td>
                <td className="text-right">
                  <span className="project-budget">{item.butce}</span>
                </td>
              </tr>
            ))}
            <tr className="total-row">
              <td colSpan="2">TOPLAM</td>
              <td className="text-center">
                <span className="project-count">{totalProjects}</span>
              </td>
              <td className="text-right">
                <span className="project-budget">{totalBudget.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FacultyTable; 